import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import expenseService from '@/services/expenseService'

const initialState = {
  myClaims: [],
  allClaims: [],
  currentClaim: null,
  categories: [],
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    perPage: 20,
    totalPages: 0,
  },
}

export const fetchMyClaims = createAsyncThunk(
  'expense/fetchMyClaims',
  async (params, { rejectWithValue }) => {
    try {
      const response = await expenseService.getMyClaims(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入費用申請失敗')
    }
  }
)

export const fetchAllClaims = createAsyncThunk(
  'expense/fetchAllClaims',
  async (params, { rejectWithValue }) => {
    try {
      const response = await expenseService.getAllClaims(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入費用申請失敗')
    }
  }
)

export const fetchClaimDetail = createAsyncThunk(
  'expense/fetchClaimDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await expenseService.getClaimDetail(id)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入費用詳情失敗')
    }
  }
)

export const fetchCategories = createAsyncThunk(
  'expense/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await expenseService.getCategories()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '載入費用類別失敗')
    }
  }
)

export const createClaim = createAsyncThunk(
  'expense/createClaim',
  async (data, { rejectWithValue }) => {
    try {
      const response = await expenseService.createClaim(data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '建立費用申請失敗')
    }
  }
)

export const submitClaim = createAsyncThunk(
  'expense/submitClaim',
  async (id, { rejectWithValue }) => {
    try {
      const response = await expenseService.submitClaim(id)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '提交失敗')
    }
  }
)

export const approveClaim = createAsyncThunk(
  'expense/approveClaim',
  async (id, { rejectWithValue }) => {
    try {
      const response = await expenseService.approveClaim(id)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '核准失敗')
    }
  }
)

export const rejectClaim = createAsyncThunk(
  'expense/rejectClaim',
  async ({ id, notes }, { rejectWithValue }) => {
    try {
      const response = await expenseService.rejectClaim(id, notes)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '拒絕失敗')
    }
  }
)

export const deleteClaim = createAsyncThunk(
  'expense/deleteClaim',
  async (id, { rejectWithValue }) => {
    try {
      const response = await expenseService.deleteClaim(id)
      return { ...response.data, id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '刪除失敗')
    }
  }
)

const expenseSlice = createSlice({
  name: 'expense',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentClaim: (state) => {
      state.currentClaim = null
    },
  },
  extraReducers: (builder) => {
    builder
      // My Claims
      .addCase(fetchMyClaims.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyClaims.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload.data
        state.myClaims = data.items || data
        if (data.pagination) {
          state.pagination = data.pagination
        }
      })
      .addCase(fetchMyClaims.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // All Claims
      .addCase(fetchAllClaims.fulfilled, (state, action) => {
        const data = action.payload.data
        state.allClaims = data.items || data
        if (data.pagination) {
          state.pagination = data.pagination
        }
      })
      // Detail
      .addCase(fetchClaimDetail.fulfilled, (state, action) => {
        state.currentClaim = action.payload.data
      })
      // Categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.data || []
      })
      // Create
      .addCase(createClaim.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createClaim.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(createClaim.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Submit
      .addCase(submitClaim.fulfilled, (state, action) => {
        const id = action.payload.id
        state.myClaims = state.myClaims.map(c =>
          c.id == id ? { ...c, status: 'pending' } : c
        )
      })
      // Approve
      .addCase(approveClaim.fulfilled, (state, action) => {
        const id = action.payload.id
        state.allClaims = state.allClaims.map(c =>
          c.id == id ? { ...c, status: 'approved' } : c
        )
      })
      // Reject
      .addCase(rejectClaim.fulfilled, (state, action) => {
        const id = action.payload.id
        state.allClaims = state.allClaims.map(c =>
          c.id == id ? { ...c, status: 'rejected' } : c
        )
      })
      // Delete
      .addCase(deleteClaim.fulfilled, (state, action) => {
        const id = action.payload.id
        state.myClaims = state.myClaims.filter(c => c.id != id)
        state.allClaims = state.allClaims.filter(c => c.id != id)
      })
  },
})

export const { clearError, clearCurrentClaim } = expenseSlice.actions
export default expenseSlice.reducer
